<template>
  <div class="new project h-auto p-3 pb-10">
    <nav class="flex flex-col" aria-label="Breadcrumb">
      <ol class="inline-flex items-center space-x-1 md:space-x-3">
        <li class="inline-flex items-center">
          <router-link
            to="/"
            class="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <svg
              class="mr-2 w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
              ></path>
            </svg>
            Home
          </router-link>
        </li>
        <li>
          <div class="flex items-center">
            <svg
              class="w-6 h-6 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <router-link
              to="/new/add"
              class="ml-1 text-sm font-medium text-gray-700 hover:text-gray-900 md:ml-2 dark:text-gray-400 dark:hover:text-white"
              >Yachts | Add Yacht</router-link
            >
          </div>
        </li>
      </ol>

      <div class="mt-5 w-full">
        <h1 class="text-2xl text-gray-900 font-medium dark:text-gray-200">
          Add Yacht
        </h1>
        <p class="mt-1 text-sm font-normal text-gray-400">
          Add your new yacht informations.
        </p>
      </div>
    </nav>

    <div
      class="bg-white dark:bg-gray-800 rounded-md p-5 w-full box-border border dark:border-gray-700 mt-5"
    >
      <form @submit.prevent="addYacht">
        <div class="informations flex flex-col gap-6 mb-5">
          <!-- Yacht Title -->
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1 mb-3">
              <h3 class="text-lg text-primary font-medium">Yacht Title</h3>
              <hr
                class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600"
              />
            </div>
            <Input
              type="text"
              placeholder="Title"
              v-model="addYachtViewModel.yacht.title"
              :isRequired="true"
            ></Input>
          </div>

          <!-- Header Slider Section -->
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1 mb-3">
              <h3 class="text-lg text-primary font-medium">Header Slider</h3>
              <hr
                class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600"
              />
            </div>
            <div>
              <div class="space-y-4">
                <div
                  class="flex flex-col gap-2"
                  v-for="(item, index) in addYachtViewModel.sliderPictures"
                  :key="index"
                >
                  <div class="flex justify-between items-end">
                    <h6
                      class="font-medium text-sm text-gray-600 dark:text-gray-400"
                    >
                      {{ `Slider Item ${index + 1}` }}
                    </h6>

                    <button
                      v-if="addYachtViewModel.sliderPictures.length > 1"
                      @click="() => deleteSliderItem(index)"
                      class="text-primary text-base border border-primary rounded-full p-1"
                      type="button"
                    >
                      <Icon icon="tabler:trash-filled" />
                    </button>
                  </div>
                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Slider Text"
                    v-model="addYachtViewModel.sliderPictures[index].title"
                    required
                  />
                  <FileUploader
                    @updatePendingFiles="
                      updatePendingFiles($event, 'slider', index)
                    "
                  ></FileUploader>
                </div>
              </div>

              <div class="flex mt-3 justify-center">
                <button
                  v-if="addYachtViewModel.sliderPictures.length < 4"
                  type="button"
                  @click="addSlider"
                  class="text-2xl text-white bg-primary rounded-md hover:bg-primary/80 transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
                >
                  <Icon icon="ic:outline-plus"></Icon>
                </button>
              </div>
            </div>
          </div>

          <!-- Project Section -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1 mb-1">
              <h3 class="text-lg text-primary font-medium">Project Section</h3>
              <hr
                class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600"
              />
            </div>
            <div class="flex flex-col gap-2 w-full">
              <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                Project Image
              </h6>
              <FileUploader
                @updatePendingFiles="updatePendingFiles($event, 'projectImage')"
              ></FileUploader>
            </div>

            <div class="flex flex-col gap-2">
              <Input
                type="textarea"
                placeholder="Project Text"
                v-model="addYachtViewModel.yacht.projectText"
                :isRequired="true"
              ></Input>
            </div>
          </div>

          <!-- Best Features Section -->
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1 mb-3">
              <h3 class="text-lg text-primary font-medium">Features Section</h3>
              <hr
                class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600"
              />
            </div>

            <div class="grid xl:grid-cols-3 grid-cols-1 xl:gap-5 gap-4">
              <!-- Box 1 -->
              <div class="flex flex-col gap-2">
                <h6
                  class="font-medium text-sm text-gray-600 dark:text-gray-400"
                >
                  Box 1
                </h6>

                <div class="flex flex-col gap-2">
                  <FileUploader
                    @updatePendingFiles="
                      updatePendingFiles($event, 'featuresBoxImage1')
                    "
                  ></FileUploader>

                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Title"
                    v-model="
                      addYachtViewModel.yacht.featuresBoxes
                        .bestFeaturesBoxOneTitle
                    "
                    required
                  />

                  <textarea
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Description"
                    v-model="
                      addYachtViewModel.yacht.featuresBoxes
                        .bestFeaturesBoxOneDescription
                    "
                    required
                  ></textarea>
                </div>
              </div>

              <!-- Box 2 -->
              <div class="flex flex-col gap-2">
                <h6
                  class="font-medium text-sm text-gray-600 dark:text-gray-400"
                >
                  Box 2
                </h6>

                <div class="flex flex-col gap-2">
                  <FileUploader
                    @updatePendingFiles="
                      updatePendingFiles($event, 'featuresBoxImage2')
                    "
                  ></FileUploader>

                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Title"
                    v-model="
                      addYachtViewModel.yacht.featuresBoxes
                        .bestFeaturesBoxTwoTitle
                    "
                    required
                  />

                  <textarea
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Description"
                    v-model="
                      addYachtViewModel.yacht.featuresBoxes
                        .bestFeaturesBoxTwoDescription
                    "
                    required
                  ></textarea>
                </div>
              </div>

              <!-- Box 3 -->
              <div class="flex flex-col gap-2">
                <h6
                  class="font-medium text-sm text-gray-600 dark:text-gray-400"
                >
                  Box 3
                </h6>

                <div class="flex flex-col gap-2">
                  <FileUploader
                    @updatePendingFiles="
                      updatePendingFiles($event, 'featuresBoxImage3')
                    "
                  ></FileUploader>

                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Title"
                    v-model="
                      addYachtViewModel.yacht.featuresBoxes
                        .bestFeaturesBoxThreeTitle
                    "
                    required
                  />

                  <textarea
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Description"
                    v-model="
                      addYachtViewModel.yacht.featuresBoxes
                        .bestFeaturesBoxThreeDescription
                    "
                    required
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Performance Details Section -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1 mb-1">
              <h3 class="text-lg text-primary font-medium">
                Performance Details
              </h3>
              <hr
                class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600"
              />
            </div>

            <Input
              type="text"
              placeholder="Description"
              v-model="addYachtViewModel.yacht.performanceDetailsDescription"
              :isRequired="true"
            ></Input>

            <div class="flex flex-col gap-4">
              <!-- Dimension -->
              <div class="flex flex-col gap-2 max-sm:gap-3">
                <div class="flex flex-col gap-2 w-full">
                  <h6
                    class="font-medium text-sm max-sm:text-base max-sm:font-semibold text-gray-600 dark:text-gray-400"
                  >
                    Dimension
                  </h6>
                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Dimension Description"
                    v-model="
                      addYachtViewModel.yacht.performaceDetails
                        .dimensionDescription
                    "
                    required
                  />
                </div>

                <div class="flex flex-col max-sm:hidden">
                  <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="inline-block min-w-full sm:px-6 lg:px-8">
                      <div
                        class="overflow-hidden border-2 rounded-md border-gray-200 dark:border-gray-700 box-border bg-gray-100 dark:bg-gray-900"
                      >
                        <table
                          class="min-w-full text-center text-sm font-light"
                        >
                          <thead
                            class="border-b-2 border-gray-200 dark:border-gray-700 box-border font-medium text-gray-600 dark:text-gray-400"
                          >
                            <tr>
                              <th
                                scope="col"
                                class="border-r-2 border-gray-200 dark:border-gray-700 box-border px-1 max-md:px-2"
                              ></th>
                              <th
                                scope="col"
                                class="border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                Title
                              </th>
                              <th
                                scope="col"
                                class="border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                Type
                              </th>
                              <th scope="col" class="px-6 py-4">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              :class="{
                                'border-b-2 border-gray-200 dark:border-gray-700 box-border':
                                  index !==
                                  addYachtViewModel.dimensions.length - 1,
                              }"
                              v-for="(
                                item, index
                              ) in addYachtViewModel.dimensions"
                            >
                              <td
                                class="whitespace-nowrap px-1 max-md:px-2 border-r-2 border-gray-200 dark:border-gray-700 box-border font-medium"
                              >
                                <button
                                  @click="
                                    () =>
                                      deletePerformanceDetailsItem(
                                        index,
                                        'dimensions'
                                      )
                                  "
                                  :class="{
                                    'text-primary/50 border-primary/50':
                                      addYachtViewModel.dimensions.length == 1,
                                  }"
                                  :disabled="
                                    addYachtViewModel.dimensions.length == 1
                                  "
                                  class="text-primary text-base border border-primary rounded-full p-1"
                                  type="button"
                                >
                                  <Icon icon="tabler:trash-filled" />
                                </button>
                              </td>
                              <td
                                class="whitespace-nowrap border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Title..."
                                  v-model="
                                    addYachtViewModel.dimensions[index].title
                                  "
                                  required
                                />
                              </td>
                              <td
                                class="whitespace-nowrap border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Type..."
                                  v-model="
                                    addYachtViewModel.dimensions[index].value1
                                  "
                                  required
                                />
                              </td>
                              <td class="whitespace-nowrap">
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Value..."
                                  v-model="
                                    addYachtViewModel.dimensions[index].value2
                                  "
                                  required
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="hidden max-sm:flex flex-col gap-2"
                  v-for="(item, index) in addYachtViewModel.dimensions"
                  :key="index"
                >
                  <div class="flex justify-between items-end">
                    <h6
                      class="font-medium text-sm text-gray-600 dark:text-gray-400"
                    >
                      {{ `Table Row ${index + 1}` }}
                    </h6>

                    <button
                      v-if="addYachtViewModel.dimensions.length > 1"
                      @click="
                        () => deletePerformanceDetailsItem(index, 'dimensions')
                      "
                      class="text-primary text-base border border-primary rounded-full p-1"
                      type="button"
                    >
                      <Icon icon="tabler:trash-filled" />
                    </button>
                  </div>
                  <div
                    class="border-2 rounded-md border-gray-200 dark:border-gray-700 box-border bg-gray-100 dark:bg-gray-900"
                  >
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Title"
                        v-model="addYachtViewModel.dimensions[index].title"
                        required
                      />
                    </div>
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Type"
                        v-model="addYachtViewModel.dimensions[index].value1"
                        required
                      />
                    </div>
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Value"
                        v-model="addYachtViewModel.dimensions[index].value2"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div class="flex justify-center">
                  <button
                    type="button"
                    @click="() => addItemToPerformanceDetails('dimensions')"
                    class="text-2xl text-white bg-primary rounded-md hover:bg-primary/80 transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
                  >
                    <Icon icon="ic:outline-plus"></Icon>
                  </button>
                </div>
              </div>

              <!-- Prformance -->
              <div class="flex flex-col gap-2 max-sm:gap-3">
                <div class="flex flex-col gap-2 w-full">
                  <h6
                    class="font-medium text-sm max-sm:text-base max-sm:font-semibold text-gray-600 dark:text-gray-400"
                  >
                    Prformance
                  </h6>
                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Performance Description"
                    v-model="
                      addYachtViewModel.yacht.performaceDetails
                        .performanceDescription
                    "
                    required
                  />
                </div>

                <div class="flex flex-col max-sm:hidden">
                  <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="inline-block min-w-full sm:px-6 lg:px-8">
                      <div
                        class="overflow-hidden border-2 rounded-md border-gray-200 dark:border-gray-700 box-border bg-gray-100 dark:bg-gray-900"
                      >
                        <table
                          class="min-w-full text-center text-sm font-light"
                        >
                          <thead
                            class="border-b-2 border-gray-200 dark:border-gray-700 box-border font-medium text-gray-600 dark:text-gray-400"
                          >
                            <tr>
                              <th
                                scope="col"
                                class="border-r-2 px-1 max-md:px-2 border-gray-200 dark:border-gray-700 box-border"
                              ></th>
                              <th
                                scope="col"
                                class="border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                Title
                              </th>
                              <th
                                scope="col"
                                class="border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                Type
                              </th>
                              <th scope="col" class="px-6 py-4">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              :class="{
                                'border-b-2 border-gray-200 dark:border-gray-700 box-border':
                                  index !==
                                  addYachtViewModel.performances.length - 1,
                              }"
                              v-for="(
                                item, index
                              ) in addYachtViewModel.performances"
                            >
                              <td
                                class="whitespace-nowrap px-1 max-md:px-2 border-r-2 border-gray-200 dark:border-gray-700 box-border font-medium"
                              >
                                <button
                                  @click="
                                    () =>
                                      deletePerformanceDetailsItem(
                                        index,
                                        'performances'
                                      )
                                  "
                                  :class="{
                                    'text-primary/50 border-primary/50':
                                      addYachtViewModel.performances.length ==
                                      1,
                                  }"
                                  :disabled="
                                    addYachtViewModel.performances.length == 1
                                  "
                                  class="text-primary text-base border border-primary rounded-full p-1"
                                  type="button"
                                >
                                  <Icon icon="tabler:trash-filled" />
                                </button>
                              </td>
                              <td
                                class="whitespace-nowrap border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Title..."
                                  v-model="
                                    addYachtViewModel.performances[index].title
                                  "
                                  required
                                />
                              </td>
                              <td
                                class="whitespace-nowrap border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Type..."
                                  v-model="
                                    addYachtViewModel.performances[index].value1
                                  "
                                  required
                                />
                              </td>
                              <td class="whitespace-nowrap">
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Value..."
                                  v-model="
                                    addYachtViewModel.performances[index].value2
                                  "
                                  required
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="hidden max-sm:flex flex-col gap-2"
                  v-for="(item, index) in addYachtViewModel.performances"
                  :key="index"
                >
                  <div class="flex justify-between items-end">
                    <h6
                      class="font-medium text-sm text-gray-600 dark:text-gray-400"
                    >
                      {{ `Table Row ${index + 1}` }}
                    </h6>

                    <button
                      v-if="addYachtViewModel.performances.length > 1"
                      @click="
                        () =>
                          deletePerformanceDetailsItem(index, 'performances')
                      "
                      class="text-primary text-base border border-primary rounded-full p-1"
                      type="button"
                    >
                      <Icon icon="tabler:trash-filled" />
                    </button>
                  </div>
                  <div
                    class="border-2 rounded-md border-gray-200 dark:border-gray-700 box-border bg-gray-100 dark:bg-gray-900"
                  >
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Title"
                        v-model="addYachtViewModel.performances[index].title"
                        required
                      />
                    </div>
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Type"
                        v-model="addYachtViewModel.performances[index].value1"
                        required
                      />
                    </div>
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Value"
                        v-model="addYachtViewModel.performances[index].value2"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div class="flex justify-center">
                  <button
                    type="button"
                    @click="() => addItemToPerformanceDetails('performances')"
                    class="text-2xl text-white bg-primary rounded-md hover:bg-primary/80 transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
                  >
                    <Icon icon="ic:outline-plus"></Icon>
                  </button>
                </div>
              </div>

              <!-- Population System -->
              <div class="flex flex-col gap-2 max-sm:gap-3">
                <div class="flex flex-col gap-2 w-full">
                  <h6
                    class="font-medium text-sm max-sm:text-base max-sm:font-semibold text-gray-600 dark:text-gray-400"
                  >
                    Population System
                  </h6>
                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Population System Description"
                    v-model="
                      addYachtViewModel.yacht.performaceDetails
                        .populationSystemDescription
                    "
                    required
                  />
                </div>

                <div class="flex flex-col max-sm:hidden">
                  <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="inline-block min-w-full sm:px-6 lg:px-8">
                      <div
                        class="overflow-hidden border-2 rounded-md border-gray-200 dark:border-gray-700 box-border bg-gray-100 dark:bg-gray-900"
                      >
                        <table
                          class="min-w-full text-center text-sm font-light"
                        >
                          <thead
                            class="border-b-2 border-gray-200 dark:border-gray-700 box-border font-medium text-gray-600 dark:text-gray-400"
                          >
                            <tr>
                              <th
                                scope="col"
                                class="border-r-2 px-1 max-md:px-2 border-gray-200 dark:border-gray-700 box-border"
                              ></th>
                              <th
                                scope="col"
                                class="border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                Title
                              </th>
                              <th
                                scope="col"
                                class="border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                Type
                              </th>
                              <th scope="col" class="px-6 py-4">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              :class="{
                                'border-b-2 border-gray-200 dark:border-gray-700 box-border':
                                  index !==
                                  addYachtViewModel.populationSystems.length -
                                    1,
                              }"
                              v-for="(
                                item, index
                              ) in addYachtViewModel.populationSystems"
                            >
                              <td
                                class="whitespace-nowrap px-1 max-md:px-2 border-r-2 border-gray-200 dark:border-gray-700 box-border font-medium"
                              >
                                <button
                                  @click="
                                    () =>
                                      deletePerformanceDetailsItem(
                                        index,
                                        'populationSystems'
                                      )
                                  "
                                  :class="{
                                    'text-primary/50 border-primary/50':
                                      addYachtViewModel.populationSystems
                                        .length == 1,
                                  }"
                                  :disabled="
                                    addYachtViewModel.populationSystems
                                      .length == 1
                                  "
                                  class="text-primary text-base border border-primary rounded-full p-1"
                                  type="button"
                                >
                                  <Icon icon="tabler:trash-filled" />
                                </button>
                              </td>
                              <td
                                class="whitespace-nowrap border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Title..."
                                  v-model="
                                    addYachtViewModel.populationSystems[index]
                                      .title
                                  "
                                  required
                                />
                              </td>
                              <td
                                class="whitespace-nowrap border-r-2 border-gray-200 dark:border-gray-700 box-border"
                              >
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Type..."
                                  v-model="
                                    addYachtViewModel.populationSystems[index]
                                      .value1
                                  "
                                  required
                                />
                              </td>
                              <td class="whitespace-nowrap">
                                <input
                                  type="text"
                                  class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                                  placeholder="Value..."
                                  v-model="
                                    addYachtViewModel.populationSystems[index]
                                      .value2
                                  "
                                  required
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="hidden max-sm:flex flex-col gap-2"
                  v-for="(item, index) in addYachtViewModel.populationSystems"
                  :key="index"
                >
                  <div class="flex justify-between items-end">
                    <h6
                      class="font-medium text-sm text-gray-600 dark:text-gray-400"
                    >
                      {{ `Table Row ${index + 1}` }}
                    </h6>

                    <button
                      v-if="addYachtViewModel.populationSystems.length > 1"
                      @click="
                        () =>
                          deletePerformanceDetailsItem(
                            index,
                            'populationSystems'
                          )
                      "
                      class="text-primary text-base border border-primary rounded-full p-1"
                      type="button"
                    >
                      <Icon icon="tabler:trash-filled" />
                    </button>
                  </div>
                  <div
                    class="border-2 rounded-md border-gray-200 dark:border-gray-700 box-border bg-gray-100 dark:bg-gray-900"
                  >
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Title"
                        v-model="
                          addYachtViewModel.populationSystems[index].title
                        "
                        required
                      />
                    </div>
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Type"
                        v-model="
                          addYachtViewModel.populationSystems[index].value1
                        "
                        required
                      />
                    </div>
                    <div
                      class="whitespace-nowrap border-b-2 border-gray-200 dark:border-gray-700 box-border"
                    >
                      <input
                        type="text"
                        class="overflow-hidden flex px-3 py-4 w-full h-full text-black dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none"
                        placeholder="Value"
                        v-model="
                          addYachtViewModel.populationSystems[index].value2
                        "
                        required
                      />
                    </div>
                  </div>
                </div>

                <div class="flex justify-center">
                  <button
                    type="button"
                    @click="
                      () => addItemToPerformanceDetails('populationSystems')
                    "
                    class="text-2xl text-white bg-primary rounded-md hover:bg-primary/80 transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
                  >
                    <Icon icon="ic:outline-plus"></Icon>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Gallery Section -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1 mb-1">
              <h3 class="text-lg text-primary font-medium">Gallery Section</h3>
              <hr
                class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600"
              />
            </div>
            <div class="flex flex-col gap-2 w-full">
              <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                Gallery Pictures
              </h6>

              <FileUploader
                @updatePendingFiles="updatePendingFiles($event, 'galleries')"
                :allowMultiple="true"
              ></FileUploader>
              <span class="italic text-sm text-black dark:text-white"
                >* Notice: You can select multiple images for the gallery
                section by holding the 'Ctrl' key.</span
              >
            </div>
          </div>

          <!-- Maps Section -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1 mb-1">
              <h3 class="text-lg text-primary font-medium">Maps Section</h3>
              <hr
                class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600"
              />
            </div>
            <div class="flex max-xl:flex-col xl:gap-5 gap-4">
              <div class="flex flex-col gap-2 w-full">
                <h6
                  class="font-medium text-sm text-gray-600 dark:text-gray-400"
                >
                  Map 1
                </h6>
                <FileUploader
                  @updatePendingFiles="updatePendingFiles($event, 'map1')"
                ></FileUploader>
              </div>

              <div class="flex flex-col gap-2 w-full">
                <h6
                  class="font-medium text-sm text-gray-600 dark:text-gray-400"
                >
                  Map 2
                </h6>
                <FileUploader
                  @updatePendingFiles="updatePendingFiles($event, 'map2')"
                ></FileUploader>
              </div>
            </div>
          </div>

          <!-- Our Project Section -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1 mb-1">
              <h3 class="text-lg text-primary font-medium">Our Project</h3>
              <hr
                class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600"
              />
            </div>
            <div class="grid md:grid-cols-3 grid-cols-1 md:gap-5 gap-3">
              <Input
                type="text"
                placeholder="Length"
                v-model="addYachtViewModel.yacht.yachtLength"
                :isRequired="true"
              ></Input>

              <Input
                type="text"
                placeholder="Max Speed"
                v-model="addYachtViewModel.yacht.maxSpeed"
                :isRequired="true"
              ></Input>

              <Input
                type="text"
                placeholder="Main Engine"
                v-model="addYachtViewModel.yacht.mainEngine"
                :isRequired="true"
              ></Input>
            </div>
          </div>
        </div>

        <hr class="w-full border dark:border-gray-700" />
        <div class="wrapper-button flex justify-end mt-5">
          <button
            v-if="!isAdding"
            type="submit"
            class="text-white bg-primary rounded-md hover:bg-primary/80 transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
          >
            Save
          </button>

          <button
            v-else
            disabled
            type="button"
            class="text-white bg-primary rounded-md opacity-60 cursor-default transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
          >
            <svg
              role="status"
              class="inline w-4 h-4 mr-1 text-white animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#E5E7EB"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
            Uploading...
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import FileUploader from "@/components/FileUploader.vue";
import axios from "axios";
import { Icon } from "@iconify/vue";
import Input from "@/components/Input.vue";

export default {
  components: {
    FileUploader,
    Input,
    Icon,
  },

  data() {
    return {
      addYachtViewModel: {
        projectImage: null,
        featuresBoxImage1: null,
        featuresBoxImage2: null,
        featuresBoxImage3: null,
        map1: null,
        map2: null,
        dimensions: [
          {
            title: "",
            value1: "",
            value2: "",
          },
        ],
        performances: [
          {
            title: "",
            value1: "",
            value2: "",
          },
        ],
        populationSystems: [
          {
            title: "",
            value1: "",
            value2: "",
          },
        ],
        galleries: [],
        sliderPictures: [{ file: null, title: "" }],
        yacht: {
          title: "",
          projectText: "",
          performanceDetailsDescription: "",
          featuresBoxes: {
            bestFeaturesBoxOneTitle: "",
            bestFeaturesBoxOneDescription: "",
            bestFeaturesBoxTwoTitle: "",
            bestFeaturesBoxTwoDescription: "",
            bestFeaturesBoxThreeTitle: "",
            bestFeaturesBoxThreeDescription: "",
          },
          yachtLength: "",
          maxSpeed: "",
          mainEngine: "",
          performaceDetails: {
            dimensionDescription: "",
            performanceDescription: "",
            populationSystemDescription: "",
          },
        },
      },
      isAdding: false,
    };
  },

  methods: {
    updatePendingFiles: function (files, imageType, sliderIndex = 0) {
      console.log(files);
      if (files) {
        if (imageType == "featuresBoxImage1") {
          this.addYachtViewModel.featuresBoxImage1 = files[0]?.file;
        }

        if (imageType == "featuresBoxImage2") {
          this.addYachtViewModel.featuresBoxImage2 = files[0]?.file;
        }

        if (imageType == "featuresBoxImage3") {
          this.addYachtViewModel.featuresBoxImage3 = files[0]?.file;
        }

        if (imageType == "map1") {
          this.addYachtViewModel.map1 = files[0]?.file;
        }

        if (imageType == "map2") {
          this.addYachtViewModel.map2 = files[0]?.file;
        }

        if (imageType == "slider") {
          this.addYachtViewModel.sliderPictures[sliderIndex].file =
            files[0]?.file;
        }

        if (imageType == "projectImage") {
          this.addYachtViewModel.projectImage = files[0]?.file;
        }

        if (imageType == "galleries") {
          this.addYachtViewModel.galleries = files;
        }
      }
    },
    addItemToPerformanceDetails: function (type) {
      if (type == "dimensions") {
        this.addYachtViewModel.dimensions.push({
          title: "",
          value1: "",
          value2: "",
        });
      }

      if (type == "performances") {
        this.addYachtViewModel.performances.push({
          title: "",
          value1: "",
          value2: "",
        });
      }

      if (type == "populationSystems") {
        this.addYachtViewModel.populationSystems.push({
          title: "",
          value1: "",
          value2: "",
        });
      }
    },
    addSlider: function () {
      this.addYachtViewModel.sliderPictures.push({ file: null, title: "" });
    },
    addYacht: async function () {
      var bodyFormData = new FormData();

      for (
        let index = 0;
        index < this.addYachtViewModel.sliderPictures.length;
        index++
      ) {
        const element = this.addYachtViewModel.sliderPictures[index];

        if (!element.file) {
          this.$store.commit("showToast", {
            id: "myToast",
            type: "error",
            title: `Slider Image ${index + 1} Is Required!`,
            body: `You must choice any image for slider image ${index + 1}!`,
          });
          return;
        }

        bodyFormData.append(
          "SliderViewModel.ImageFiles",
          element.file,
          element.file.name
        );

        bodyFormData.append("SliderViewModel.Descriptions", element.title);
      }

      bodyFormData.append("Title", this.addYachtViewModel.yacht.title);
      bodyFormData.append(
        "ProjectText",
        this.addYachtViewModel.yacht.projectText
      );

      bodyFormData.append(
        "BestFeaturesBoxOneTitle",
        this.addYachtViewModel.yacht.featuresBoxes.bestFeaturesBoxOneTitle
      );

      bodyFormData.append(
        "BestFeaturesBoxOneDescription",
        this.addYachtViewModel.yacht.featuresBoxes.bestFeaturesBoxOneDescription
      );

      bodyFormData.append(
        "BestFeaturesBoxTwoTitle",
        this.addYachtViewModel.yacht.featuresBoxes.bestFeaturesBoxTwoTitle
      );

      bodyFormData.append(
        "BestFeaturesBoxTwoDescription",
        this.addYachtViewModel.yacht.featuresBoxes.bestFeaturesBoxTwoDescription
      );

      bodyFormData.append(
        "BestFeaturesBoxThreeTitle",
        this.addYachtViewModel.yacht.featuresBoxes.bestFeaturesBoxThreeTitle
      );

      bodyFormData.append(
        "BestFeaturesBoxThreeDescription",
        this.addYachtViewModel.yacht.featuresBoxes
          .bestFeaturesBoxThreeDescription
      );

      bodyFormData.append(
        "PerformanceDetailsDescription",
        this.addYachtViewModel.yacht.performanceDetailsDescription
      );

      bodyFormData.append(
        "DimensionDescription",
        this.addYachtViewModel.yacht.performaceDetails.dimensionDescription
      );

      bodyFormData.append(
        "PerformanceDescription",
        this.addYachtViewModel.yacht.performaceDetails.performanceDescription
      );

      bodyFormData.append(
        "PopulationSystemDescription",
        this.addYachtViewModel.yacht.performaceDetails
          .populationSystemDescription
      );

      bodyFormData.append("Length", this.addYachtViewModel.yacht.yachtLength);

      bodyFormData.append("MaxSpeed", this.addYachtViewModel.yacht.maxSpeed);

      bodyFormData.append(
        "MainEngine",
        this.addYachtViewModel.yacht.mainEngine
      );

      if (!this.addYachtViewModel.projectImage) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Project Image Is Required!",
          body: "You must choice any image for project image!",
        });
        return;
      }

      bodyFormData.append(
        "ProjectImageFile",
        this.addYachtViewModel.projectImage,
        this.addYachtViewModel.projectImage.name
      );

      this.addYachtViewModel.dimensions.forEach((item) => {
        bodyFormData.append("Dimensions", JSON.stringify(item));
      });

      this.addYachtViewModel.performances.forEach((item) => {
        bodyFormData.append("Performances", JSON.stringify(item));
      });

      this.addYachtViewModel.populationSystems.forEach((item) => {
        bodyFormData.append("PopulationSystems", JSON.stringify(item));
      });

      if (!this.addYachtViewModel.featuresBoxImage1) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Features Box Image 1 Is Required!",
          body: "You must choice any image for features box image 1!",
        });
        return;
      }

      bodyFormData.append(
        "BestFeaturesBoxOneImageFile",
        this.addYachtViewModel.featuresBoxImage1,
        this.addYachtViewModel.featuresBoxImage1.name
      );

      if (!this.addYachtViewModel.featuresBoxImage2) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Features Box Image 2 Is Required!",
          body: "You must choice any image for features box image 2!",
        });
        return;
      }

      bodyFormData.append(
        "BestFeaturesBoxTwoImageFile",
        this.addYachtViewModel.featuresBoxImage2,
        this.addYachtViewModel.featuresBoxImage2.name
      );

      if (!this.addYachtViewModel.featuresBoxImage3) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Features Box Image 3 Is Required!",
          body: "You must choice any image for features box image 3!",
        });
        return;
      }

      bodyFormData.append(
        "BestFeaturesBoxThreeImageFile",
        this.addYachtViewModel.featuresBoxImage3,
        this.addYachtViewModel.featuresBoxImage3.name
      );

      if (this.addYachtViewModel.galleries.length == 0) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: `Gallery Image Is Required!`,
          body: "You must choice one image at least for gallery images!",
        });
        return;
      }

      this.addYachtViewModel.galleries.forEach((element) => {
        bodyFormData.append(
          "GalleryViewModel.Files",
          element.file,
          element.file.name
        );
      });

      if (!this.addYachtViewModel.map1) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Map Image 1 Is Required!",
          body: "You must choice any image for map image 1!",
        });
        return;
      }

      bodyFormData.append(
        "MapPictureFile1",
        this.addYachtViewModel.map1,
        this.addYachtViewModel.map1.name
      );

      if (!this.addYachtViewModel.map2) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Map Image 2 Is Required!",
          body: "You must choice any image for map image 2!",
        });
        return;
      }

      bodyFormData.append(
        "MapPictureFile2",
        this.addYachtViewModel.map2,
        this.addYachtViewModel.map2.name
      );

      this.isAdding = true;

      let options = {
        headers: {
          "Content-Type": "multipart/form-data",
          isNeedJwtAuth: true,
        },
      };

      await axios
        .post("Yachts", bodyFormData, options)
        .then((response) => {
          this.isAdding = false;

          console.log(response);

          window.scrollTo(0, 0);

          this.$store.commit("showToast", {
            id: "myToast",
            type: "success",
            title: "Add Successful!",
            body: "The adition was successful!",
          });
        })
        .catch((error) => {
          this.isAdding = false;
          if (error.response?.data?.isSuccess == false) {
            this.$store.commit("showToast", {
              id: "myToast",
              type: "error",
              title: "Add Failed!",
              body: error.response?.data?.messages[0],
            });
          }
          console.log(error);
          console.log(error.response);
        });
    },
    deleteSliderItem(index) {
      this.addYachtViewModel.sliderPictures.splice(index, 1);
    },
    deletePerformanceDetailsItem(index, type) {
      if (type == "dimensions") {
        this.addYachtViewModel.dimensions.splice(index, 1);
      } else if (type == "performances") {
        this.addYachtViewModel.performances.splice(index, 1);
      } else if (type == "populationSystems") {
        this.addYachtViewModel.populationSystems.splice(index, 1);
      }
    },
  },
};
</script>
